import type { IVector3 } from '@seddi/math-v2';
import { type ModuleThread, Thread, spawn } from 'threads';

import type { DomImage } from './domImagesContext';
import type { ImageCategory } from './recolorTypes';

//@ts-expect-error worker import
import RecolorThreadWorker, {
  type RecolorMethods,
} from './recolorThread.worker';

/**
 * Recolor thread tiny wrapper.
 */
export class RecolorThread {
  /** The thread. */
  private thread: ModuleThread<RecolorMethods>;

  /**
   * Spawn thread.
   */
  async run(): Promise<void> {
    const thread = await spawn(new RecolorThreadWorker());
    await thread.init();
    this.thread = thread as unknown as ModuleThread<RecolorMethods>;
  }

  /**
   * Loads an image for recoloring.
   * @param image - Image.
   * @param category - Image category.
   * @param paletteSize - Palette size.
   * @returns the image palette.
   */
  loadImage(
    image: DomImage,
    category: ImageCategory,
    paletteSize: number,
  ): Promise<IVector3[]> {
    return this.thread.loadImage(image, category, paletteSize);
  }

  /**
   * Recolors a previously loaded image.
   * @param id Image ID.
   * @param color Target color.
   * @returns The recolored pixels data.
   */
  recolorImageFromRgb(id: string, color: IVector3): Promise<Uint8Array> {
    return this.thread.recolorImageFromRgb(id, color);
  }

  /**
   * Recolors a previously loaded image.
   * @param id Image ID.
   * @param palette Target palette.
   * @returns The recolored pixels data.
   */
  recolorImageFromPalette(
    id: string,
    palette: IVector3[],
  ): Promise<Uint8Array> {
    return this.thread.recolorImageFromPalette(id, palette);
  }

  /**
   * Releases an image.
   * @param id Image ID.
   */
  releaseImage(id: string): Promise<void> {
    return this.thread.releaseImage(id);
  }

  /**
   * Terminate thread.
   */
  terminate(): Promise<void> {
    return Thread.terminate(this.thread);
  }
}
